import React, { useState, useRef, useEffect } from 'react';
import { User, History, LogOut, ChevronDown, Copy, Check, Coins, Shield, Lock, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cookieManager } from '../../utils/cookieManager.js';
import { getApiBaseUrl } from '../../utils/apiConfig.js';
import ChangePasswordForm from '../auth/ChangePasswordForm.js';

// 用户中心组件
const UserCenter = ({ applicationId, onLogout, onHistoryClick, isInHeader = false }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const dropdownRef = useRef(null);

  // 复制Application ID到剪贴板
  const copyApplicationId = async () => {
    if (applicationId) {
      try {
        await navigator.clipboard.writeText(applicationId);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000); // 2秒后重置状态
      } catch (error) {
        console.error('复制失败:', error);
        // 如果剪贴板API不可用，尝试使用传统方法
        try {
          const textArea = document.createElement('textarea');
          textArea.value = applicationId;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          setIsCopied(true);
          setTimeout(() => setIsCopied(false), 2000);
        } catch (fallbackError) {
          console.error('备用复制方法也失败:', fallbackError);
        }
      }
    }
  };

  // 点击外部关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // 🆕 获取用户数据（用于权限检查）
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const API = getApiBaseUrl();
        const res = await fetch(`${API}/auth/me`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setUserData(data);
          console.log('✅ 用户数据获取成功:', data);
        }
      } catch (err) {
        console.error('获取用户数据失败:', err);
      }
    };
    fetchUserData();
  }, []);

  // 获取用户邮箱
  const getUserEmail = () => {
    try {
      return cookieManager.getUserEmail() || '用户';
    } catch (error) {
      return '用户';
    }
  };

  const userEmail = getUserEmail();
  const navigate = useNavigate();

  return (
    <div className={`flex items-center space-x-3 ${isInHeader ? '' : 'fixed top-4 right-4 z-50'}`}>
      {/* Application ID 显示 */}
      {applicationId && (
        <div className="bg-blue-100 border border-blue-300 text-blue-800 px-3 py-2 rounded-lg text-sm font-medium shadow-sm flex items-center space-x-2">
          <span className="font-semibold">ID:</span> 
          <span className="font-mono">{applicationId}</span>
          <button
            onClick={copyApplicationId}
            className="p-1 rounded hover:bg-blue-200 transition-colors"
            title={isCopied ? "已复制!" : "复制Application ID"}
          >
            {isCopied ? (
              <Check className="w-3 h-3 text-green-600" />
            ) : (
              <Copy className="w-3 h-3 text-blue-600" />
            )}
          </button>
        </div>
      )}

      {/* 用户中心下拉菜单 */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="bg-white border border-gray-300 hover:border-blue-400 text-gray-700 hover:text-blue-600 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 shadow-lg hover:shadow-xl flex items-center space-x-2"
        >
          <User className="w-4 h-4" />
          <span className="hidden sm:inline">用户中心</span>
          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* 下拉菜单 */}
        {isDropdownOpen && (
          <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-xl z-50 overflow-hidden">
            {/* 用户信息头部 */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center">
                  <User className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {userEmail}
                  </p>
                  <p className="text-xs text-gray-500">
                    已登录
                  </p>
                </div>
              </div>
            </div>

            {/* 菜单选项 */}
            <div className="py-2">
              {/* 积分中心 */}
              <button
                onClick={() => {
                  setIsDropdownOpen(false);
                  navigate('/points');
                }}
                className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors duration-200 flex items-center space-x-3"
              >
                <Coins className="w-4 h-4" />
                <div>
                  <div className="font-medium">积分中心</div>
                  <div className="text-xs text-gray-500">查看余额与积分明细</div>
                </div>
              </button>

              {/* 分销商中心 */}
              <button
                onClick={() => {
                  setIsDropdownOpen(false);
                  navigate('/commission/info');
                }}
                className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-purple-600 transition-colors duration-200 flex items-center space-x-3"
              >
                <Users className="w-4 h-4" />
                <div>
                  <div className="font-medium">分销商中心</div>
                  <div className="text-xs text-gray-500">查看邀请码与佣金</div>
                </div>
              </button>

              {/* 管理后台入口（简版） */}
              {/* 🆕 分销商审核（仅管理员） */}
              {(userData?.role === 'admin') && (
                <button
                  onClick={() => {
                    setIsDropdownOpen(false);
                    console.log('userData:', userData);
                    navigate('/admin/distributors');
                  }}
                  className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-purple-600 transition-colors duration-200 flex items-center space-x-3"
                  title={`当前role: ${userData?.role || 'null'}`}
                >
                  <Users className="w-4 h-4" />
                  <div>
                    <div className="font-medium">分销商审核 {userData?.role === 'admin' ? '✅' : '⚠️'}</div>
                    <div className="text-xs text-gray-500">审核分销商申请</div>
                  </div>
                </button>
              )}

              <button
                onClick={() => {
                  setIsDropdownOpen(false);
                  navigate('/admin');
                }}
                className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors duration-200 flex items-center space-x-3"
              >
                <Shield className="w-4 h-4" />
                <div>
                  <div className="font-medium">管理后台</div>
                  <div className="text-xs text-gray-500">用户列表（仅管理员关注）</div>
                </div>
              </button>

              {/* 修改密码选项 */}
              <button
                onClick={() => {
                  setIsDropdownOpen(false);
                  setIsChangePasswordOpen(true);
                }}
                className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors duration-200 flex items-center space-x-3"
              >
                <Lock className="w-4 h-4" />
                <div>
                  <div className="font-medium">修改密码</div>
                  <div className="text-xs text-gray-500">确保账户安全</div>
                </div>
              </button>

              {/* 历史表单选项 */}
	              <button
	                onClick={() => {
	                  setIsDropdownOpen(false);
	                  if (onHistoryClick) {
	                    onHistoryClick();
	                  }
	                }}
	                className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors duration-200 flex items-center space-x-3"
	              >
	                <History className="w-4 h-4" />
	                <div>
	                  <div className="font-medium">我的DS-160申请记录</div>
	                  <div className="text-xs text-gray-500">查看和管理DS-160申请记录</div>
	                </div>
	              </button>

              {/* 分割线 */}
              <div className="border-t border-gray-100 my-1"></div>

              {/* 登出选项 */}
              <button
                onClick={() => {
                  setIsDropdownOpen(false);
                  if (onLogout) {
                    onLogout();
                  }
                }}
                className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors duration-200 flex items-center space-x-3"
              >
                <LogOut className="w-4 h-4" />
                <div>
                  <div className="font-medium">退出登录</div>
                  <div className="text-xs text-red-500">安全退出当前账户</div>
                </div>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 修改密码模态框 */}
      {isChangePasswordOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full">
            <ChangePasswordForm
              onSuccess={() => {
                setIsChangePasswordOpen(false);
                // 可选：显示成功提示后刷新页面或重新登录
              }}
              onClose={() => {
                setIsChangePasswordOpen(false);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default UserCenter;
